/**
 * Whole-bill pro-rata split for direct bills, and the cost-type filter shared
 * by the assignment dropdowns.
 *
 * Cost groups arrive from /api/jt/jobs/:id/cost-groups-with-items (items on
 * `costItems`) or /api/jt/lineitems (items on `lineItems`). Each group carries
 * up to two ancestors on parentCostGroup.
 */

const itemsOf = (group) => group.costItems || group.lineItems || [];

/** Item passes the cost-type filter. An empty filter lets everything through. */
export function matchesCostTypes(item, allowedTypes) {
  if (!allowedTypes || allowedTypes.length === 0) return true;
  return allowedTypes.includes(costTypeLabel(item));
}

/** Display name for an item's JobTread cost type. */
export function costTypeLabel(item) {
  return (item?.costTypeName || '').trim() || 'No type';
}

/** Distinct cost types present in the budget, sorted, for the filter chips. */
export function listCostTypes(costGroups) {
  const seen = new Set();
  for (const g of costGroups || []) for (const item of itemsOf(g)) seen.add(costTypeLabel(item));
  return [...seen].sort((a, b) => (a === 'No type') - (b === 'No type') || a.localeCompare(b));
}

function groupChain(group) {
  const chain = [{ id: group.id, name: group.name }];
  if (group.parentCostGroup?.id) chain.push({ id: group.parentCostGroup.id, name: group.parentCostGroup.name });
  if (group.parentCostGroup?.parentCostGroup?.id) {
    chain.push({ id: group.parentCostGroup.parentCostGroup.id, name: group.parentCostGroup.parentCostGroup.name });
  }
  return chain;
}

/**
 * Every cost group (including parent groups, so "Framing" can take a bill
 * spread over its sub-groups) and every cost code (own + parent) that has at
 * least one budget item passing the type filter, with budget totals.
 */
export function buildSplitTargets(costGroups, allowedTypes = [], cleanName = (s) => s) {
  const groups = new Map();
  const codes = new Map();

  for (const group of costGroups || []) {
    const chain = groupChain(group);
    const path = chain.slice().reverse();
    for (const item of itemsOf(group)) {
      if (!matchesCostTypes(item, allowedTypes)) continue;
      const cost = Number(item.cost) || 0;

      chain.forEach((node, depth) => {
        if (!groups.has(node.id)) {
          const label = path.slice(0, path.length - depth).map(n => cleanName(n.name) || 'Unnamed').join(' › ');
          groups.set(node.id, { kind: 'costGroup', id: node.id, label, itemCount: 0, budget: 0 });
        }
        const t = groups.get(node.id);
        t.itemCount++;
        t.budget += cost;
      });

      const code = item.costCode;
      for (const c of [code, code?.parentCostCode]) {
        if (!c?.id) continue;
        if (!codes.has(c.id)) {
          codes.set(c.id, {
            kind: 'costCode',
            id: c.id,
            number: c.number,
            label: `${c.number || ''} ${c.name || ''}`.trim(),
            itemCount: 0,
            budget: 0
          });
        }
        const t = codes.get(c.id);
        t.itemCount++;
        t.budget += cost;
      }
    }
  }

  const byLabel = (a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
  return {
    groups: [...groups.values()].sort(byLabel),
    codes: [...codes.values()].sort(byLabel)
  };
}

/** Budget items covered by a target, with their group attached. */
export function itemsForTarget(costGroups, target, allowedTypes = []) {
  if (!target?.id) return [];
  const out = [];
  for (const group of costGroups || []) {
    const inGroup = target.kind === 'costGroup' && groupChain(group).some(n => n.id === target.id);
    for (const item of itemsOf(group)) {
      if (!matchesCostTypes(item, allowedTypes)) continue;
      const inCode = target.kind === 'costCode'
        && (item.costCode?.id === target.id || item.costCode?.parentCostCode?.id === target.id);
      if (inGroup || inCode) out.push({ ...item, costGroupId: group.id, costGroupName: group.name });
    }
  }
  return out;
}

/**
 * Spread `total` over `items` in proportion to each item's budget (cost).
 * Amounts are rounded to cents and the rounding remainder goes on the largest
 * share, so the parts always add back to the total exactly. With no budget on
 * any item the total is split evenly instead (`evenSplit: true`).
 */
export function computeProRataSplit(items, total) {
  const amount = Math.round((Number(total) || 0) * 100);
  if (!items?.length || amount === 0) return { lines: [], evenSplit: false };

  const budgets = items.map(i => Math.max(0, Number(i.cost) || 0));
  const budgetTotal = budgets.reduce((s, b) => s + b, 0);
  const evenSplit = budgetTotal <= 0;
  const weights = evenSplit ? items.map(() => 1) : budgets;
  const weightTotal = weights.reduce((s, w) => s + w, 0);

  const cents = weights.map(w => Math.round((amount * w) / weightTotal));
  const drift = amount - cents.reduce((s, c) => s + c, 0);
  if (drift !== 0) {
    let largest = 0;
    weights.forEach((w, i) => { if (w > weights[largest]) largest = i; });
    cents[largest] += drift;
  }

  const lines = items
    .map((item, i) => ({ item, ratio: weights[i] / weightTotal, amount: cents[i] / 100 }))
    .filter(l => l.amount !== 0);
  return { lines, evenSplit };
}
